import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Avatar from 'LegacyWeployDesignLanguage/Avatar';
import Divider from 'LegacyWeployDesignLanguage/Divider';
import { Button } from 'LegacyWeployDesignLanguage/buttons';
import { Tags } from 'LegacyWeployDesignLanguage/Tag';
import { SlidingModal } from 'LegacyWeployDesignLanguage/Modal';
import { HelpToolTip } from 'LegacyWeployDesignLanguage/ToolTip';

import { WeployeeProfile } from '../WeployeeProfile';
import WorkExperiencesSummary from './WorkExperiencesSummary';
import { BaseProfileCardContainer, BaseProfileCard, PROFILE_CARD_BORDER, DISABLED_BORDER_COLOUR } from './WeployeeSummarySkeleton';

// Stylings
const ProfileCard = styled(BaseProfileCard)`
  position: relative;

  ${props => props.selected && css`
    border: ${PROFILE_CARD_BORDER} solid ${props.theme.colors.brand.primary};
    box-shadow: 0 6px 10px 0 ${props.theme.colors.darkGrey};
  `};
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  width: 70%;
  margin-top: ${props => props.theme.space.double};
`;

const StyledPersonalDetails = styled.div`
  color: ${props => props.theme.colors.darkerGrey};
  margin-top: ${props => props.theme.space.half};
  text-align: center;
`;

const StyledAvailability = styled.span`
  vertical-align: middle;
`;

const StyledTitle = styled.h5`
  margin-top: ${props => props.theme.spacing[4]};
`;

export const StyledTags = styled(Tags)`
  margin-top: ${props => props.theme.space.half};
`;

const SlidingModalWithStyles = styled(SlidingModal)`
  max-width: 50%;
`;

const StyledInitials = styled.div`
  color: ${props => props.theme.colors.text.primaryOnDark};
  font-size: 32px;
  letter-spacing: 2px;
  line-height: 0;
`;

const StyledDisabledOverlay = styled.div`
  width: 120%;
  height: 120%;
  z-index: 3;
  background-color: ${DISABLED_BORDER_COLOUR};
  position: absolute;
  top: -${PROFILE_CARD_BORDER};
  left: -${PROFILE_CARD_BORDER};
  pointer-events: none;
`;

const StyledSubtitle = styled.div`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledAvatar = styled(Avatar)`
  margin-bottom: ${props => props.theme.spacing[1]};
`;
// End os Stylings

const NUMBER_OF_SKILLS_TO_DISPLAY = 3;

const WeployeeSummary = ({
  availabilities,
  avatarUrl,
  avatarColor,
  name,
  initials,
  workExperiences,
  educations,
  systemsKnowledges,
  technicalSkills,
  certifications,
  endorsedSkills,
  weployExperiences,
  onClick,
  onProfileView,
  selected,
  disabled,
  id,
  targetReached,
}) => {
  const [paneOpen, setPaneOpen] = useState(false);
  const [details, setDetails] = useState({});

  const mappedSystemsKnowledges = systemsKnowledges.map(systemsKnowledge => systemsKnowledge.name);
  const mappedTechnicalSkills = technicalSkills.map(technicalSkill => technicalSkill.name);
  const topThreeSystemsKnowledges = [...mappedSystemsKnowledges.slice(0, NUMBER_OF_SKILLS_TO_DISPLAY)];
  const topThreeTechnicalSkills = [...mappedTechnicalSkills.slice(0, NUMBER_OF_SKILLS_TO_DISPLAY)];
  const remainingSystemsKnowledges = mappedSystemsKnowledges.length - topThreeSystemsKnowledges.length;
  const remainingTechnicalSkills = mappedTechnicalSkills.length - topThreeTechnicalSkills.length;

  const handleClick = () => {
    setPaneOpen(true);
    setDetails({
      avatarUrl,
      workExperiences,
      name,
      initials,
      systemsKnowledges: mappedSystemsKnowledges,
      technicalSkills: mappedTechnicalSkills,
      educations,
      certifications,
      endorsedSkills,
      weployExperiences,
    });
    onProfileView({ target: { value: id } });
  };

  return (
    <React.Fragment>
      <BaseProfileCardContainer>
        <ProfileCard
          selected={selected}
          disabled={disabled}
        >
          { disabled && <StyledDisabledOverlay disabled={disabled} /> }
          <AvatarContainer>
            <StyledAvatar
              size={Avatar.SMALL}
              src={avatarUrl}
              text={
                <StyledInitials>
                  {initials}
                </StyledInitials>
              }
              backgroundColor={avatarColor}
            />
          </AvatarContainer>
          <StyledPersonalDetails>
            {name && <StyledSubtitle>{name}</StyledSubtitle>}
            <StyledAvailability>
              {`Available ${availabilities.join(' or ')}`}
            </StyledAvailability>
            <HelpToolTip text="Full-time is 5 days a week, part-time is less than 30 hours a week." />
          </StyledPersonalDetails>
          <StyledTitle>Experience</StyledTitle>
          <WorkExperiencesSummary
            workExperiences={workExperiences}
            onLinkClicked={handleClick}
            disabled
          />
          <Divider />
          <StyledTitle>Skills</StyledTitle>
          <StyledTags
            tags={remainingTechnicalSkills
              ? [...topThreeTechnicalSkills, `+${remainingTechnicalSkills} more`]
              : topThreeTechnicalSkills
            }
            tagMaxWidth="200px"
          />
          <StyledTitle>Software and Tools</StyledTitle>
          <StyledTags
            tags={remainingSystemsKnowledges
              ? [...topThreeSystemsKnowledges, `+${remainingSystemsKnowledges} more`]
              : topThreeSystemsKnowledges
            }
            tagMaxWidth="200px"
          />
          <ButtonContainer>
            <Button
              appearance={Button.SECONDARY}
              borderType={Button.ROUNDED}
              onClick={handleClick}
            >
              View Profile
            </Button>
            { !disabled &&
              <>
                <br />
                <Button
                  appearance={selected ? Button.SECONDARY : Button.PRIMARY}
                  borderType={Button.ROUNDED}
                  onClick={event => onClick({ target: event.currentTarget })}
                  disabled={!selected && targetReached}
                  value={id}
                  fullWidth
                >
                  {selected ? 'Unselect' : 'Select'}
                </Button>
              </>
            }
          </ButtonContainer>
        </ProfileCard>
      </BaseProfileCardContainer>
      <SlidingModalWithStyles
        open={paneOpen}
        onClose={() => setPaneOpen(false)}
      >
        <WeployeeProfile
          avatarColor={avatarColor}
          {...details}
        />
      </SlidingModalWithStyles>
    </React.Fragment>
  );
};

WeployeeSummary.propTypes = {
  availabilities: PropTypes.array.isRequired,
  avatarColor: PropTypes.oneOf(Avatar.COLORS),
  avatarUrl: PropTypes.string,
  initials: PropTypes.string.isRequired,
  name: PropTypes.string,
  workExperiences: PropTypes.arrayOf(PropTypes.object).isRequired,
  systemsKnowledges: PropTypes.arrayOf(PropTypes.object).isRequired,
  technicalSkills: PropTypes.arrayOf(PropTypes.object).isRequired,
  educations: PropTypes.arrayOf(PropTypes.object).isRequired,
  certifications: PropTypes.arrayOf(PropTypes.object),
  endorsedSkills: PropTypes.arrayOf(PropTypes.object),
  weployExperiences: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onProfileView: PropTypes.func.isRequired,
  targetReached: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
};

WeployeeSummary.defaultProps = {
  avatarColor: Avatar.GREEN,
  avatarUrl: null,
  name: null,
  certifications: [],
  endorsedSkills: [],
  weployExperiences: [],
  disabled: false,
};

export default WeployeeSummary;
